<template>
  <div
    class="whatsapp-chat-panel flex flex-col min-h-0 min-w-0"
    :class="[
      layout === 'column'
        ? 'lead-detail-modal-column lead-detail-modal-column--whatsapp modal-panel-column border-l border-gray-200 dark:border-gray-700 flex-[35_1_0%] w-0'
        : 'whatsapp-chat-panel--tab flex-1 h-full',
      columnClass
    ]"
  >
    <div
      v-if="!hasContactPhone"
      class="flex flex-1 flex-col items-center justify-center text-center px-6 py-10 wa-chat-scroll"
    >
      <p class="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
        {{ t(phoneMissingKey) }}
      </p>
    </div>

    <template v-else>
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 shadow-lg flex-shrink-0">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3 min-w-0">
            <div class="relative flex-shrink-0">
              <div class="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border border-white/30">
                <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </div>
              <div class="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-semibold text-white tracking-wide truncate">{{ contactName || t('kanban.leadDetail.whatsapp.contactFallback') }}</h3>
              <p class="text-sm text-white/90 font-medium truncate">{{ contactPhoneDisplay }}</p>
            </div>
          </div>
          <div class="flex items-center space-x-3 flex-shrink-0">
            <div class="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5">
              <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-sm" />
              <span class="text-xs font-medium text-white/90">Online</span>
            </div>
            <button
              type="button"
              class="flex items-center space-x-2 hover:bg-white/30 backdrop-blur-sm rounded-full px-3 py-1.5 transition-all duration-200 border"
              :class="autoScrollEnabled ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20'"
              :title="autoScrollEnabled ? t('kanban.leadDetail.whatsapp.autoScrollOn') : t('kanban.leadDetail.whatsapp.autoScrollOff')"
              @click="toggleAutoScroll"
            >
              <svg class="w-4 h-4 text-white" :class="autoScrollEnabled ? 'animate-bounce' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="autoScrollEnabled" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
              </svg>
              <span class="text-xs font-medium text-white">{{ autoScrollEnabled ? 'Fixado' : 'Livre' }}</span>
            </button>
          </div>
        </div>
      </div>

      <div ref="chatContainer" class="wa-chat-scroll flex-1 overflow-y-auto min-h-[400px]">
        <div v-if="chatMessages.length === 0" class="wa-chat-empty flex flex-col items-center justify-center h-full min-h-[360px] text-center px-6">
          <div class="wa-chat-empty__icon mb-4">
            <svg class="w-9 h-9 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
          </div>
          <h4 class="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">{{ t('kanban.leadDetail.whatsapp.emptyTitle') }}</h4>
          <p class="text-sm text-gray-600 dark:text-gray-400 max-w-xs leading-relaxed">{{ t('kanban.leadDetail.whatsapp.emptyHint') }}</p>
        </div>
        <div v-else class="wa-chat-thread px-4 py-5">
          <div
            v-for="message in chatMessages"
            :key="message.id"
            class="flex w-full mb-2"
            :class="message.sent ? 'justify-end' : 'justify-start'"
          >
            <div
              class="wa-bubble"
              :class="[
                message.sent ? 'wa-bubble--out' : 'wa-bubble--in',
                isMediaBubble(message) ? 'wa-bubble--media' : hasYoutubePreview(message) ? 'wa-bubble--link' : 'wa-bubble--text'
              ]"
            >
              <template v-if="!message.type || message.type === 'TEXT'">
                <WhatsAppYoutubePreview
                  v-for="yt in getYoutubePreviews(message.text)"
                  :key="`${message.id}-yt-${yt.videoId}`"
                  :video-id="yt.videoId"
                  :url="yt.url"
                />
                <div
                  v-if="messageTextWithoutYoutube(message.text).length"
                  class="wa-bubble__text whatsapp-message-body"
                  v-html="renderWhatsAppMessage(messageTextWithoutYoutube(message.text), message.sent)"
                />
              </template>
              <template v-else-if="message.type === 'IMAGE'">
                <div v-if="message.imageLoadFailed" class="wa-media__error text-sm p-2 rounded">
                  <p>{{ t('kanban.leadDetail.whatsapp.imageLoadFailed') }}</p>
                </div>
                <button
                  v-else
                  type="button"
                  class="wa-media__link block w-full text-left cursor-zoom-in border-0 bg-transparent p-0"
                  @click="openWaImagePreview(message)"
                >
                  <img :src="message.mediaUrl" class="wa-media__img" loading="lazy" @error="message.imageLoadFailed = true" />
                </button>
                <div
                  v-if="message.caption"
                  class="wa-bubble__text wa-bubble__text--caption whatsapp-message-body"
                  v-html="renderWhatsAppMessage(message.caption, message.sent)"
                />
              </template>
              <WhatsAppVoiceBubble
                v-else-if="message.type === 'AUDIO' && message.mediaUrl && !message.audioLoadFailed"
                :key="message.mediaUrl"
                :media-url="message.mediaUrl"
                :duration-seconds="message.durationSeconds"
                :sent="message.sent"
                :message-id="message.id"
                :timestamp-label="formatTime(message.timestamp)"
                :status="message.status"
                :delivery-icon="message.sent ? deliveryStatusIcon(message) : ''"
                :delivery-title="message.sent ? deliveryStatusTitle(message) : ''"
                @error="message.audioLoadFailed = true"
                @ready="message.audioLoadFailed = false"
              />
              <div v-else-if="message.type === 'AUDIO'" class="wa-audio wa-audio--fallback">
                <a
                  v-if="message.mediaUrl && message.audioLoadFailed"
                  :href="message.mediaUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="wa-audio__fallback text-sm underline px-3 py-2"
                >
                  {{ t('kanban.leadDetail.whatsapp.audioOpenExternal') }}
                </a>
                <p v-else class="wa-media__error text-sm px-3 py-2">
                  {{ t('kanban.leadDetail.whatsapp.audioLoadFailed') }}
                </p>
              </div>
              <a
                v-else-if="message.type === 'DOCUMENT'"
                :href="message.mediaUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="wa-doc-card"
                :class="message.sent ? 'wa-doc-card--out' : 'wa-doc-card--in'"
              >
                <div class="wa-doc-card__icon" :class="fileIconClass(message)">
                  <span class="wa-doc-card__ext">{{ fileExtension(message) }}</span>
                </div>
                <div class="wa-doc-card__body min-w-0">
                  <p class="wa-doc-card__name">{{ displayFileName(message) }}</p>
                  <p class="wa-doc-card__meta">{{ fileTypeLabel(fileExtension(message)) }}</p>
                </div>
              </a>
              <template v-else-if="message.type === 'VIDEO'">
                <div class="wa-media__video-wrap">
                  <video :src="message.mediaUrl" controls class="wa-media__video" preload="metadata" />
                </div>
              </template>
              <div
                v-if="!(message.type === 'AUDIO' && message.mediaUrl && !message.audioLoadFailed)"
                class="wa-bubble__footer"
              >
                <span class="wa-bubble__time">{{ formatTime(message.timestamp) }}</span>
                <span
                  v-if="message.sent && message.status"
                  class="wa-bubble__delivery"
                  :class="{
                    'wa-bubble__delivery--failed': message.status === 'FAILED',
                    'wa-bubble__delivery--pending': message.status === 'PENDING'
                  }"
                  :title="deliveryStatusTitle(message)"
                >{{ deliveryStatusIcon(message) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="wa-chat-composer">
        <Transition name="composer-reveal">
          <div v-if="showChatComposer" class="wa-composer-input-wrap">
            <div class="relative w-full">
              <textarea
                ref="messageInputEl"
                v-model="newMessage"
                rows="1"
                :placeholder="t('kanban.leadDetail.whatsapp.messagePlaceholder')"
                :class="messageTextareaClass"
                @keydown.enter.exact.prevent="sendMessage"
                @input="autoResizeWhatsAppMessage"
              />
              <div class="absolute right-2 bottom-2">
                <button type="button" class="wa-composer-emoji-btn" @click.stop="onToggleEmojiPicker">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
              <EmojiPickerPopover
                v-if="showEmojiPicker"
                class="absolute bottom-full right-0 mb-2 z-[10001]"
                @select="insertEmoji"
                @close="showEmojiPicker = false"
              />
            </div>
          </div>
        </Transition>
        <div class="wa-composer-toolbar">
          <div class="wa-composer-toolbar__actions relative">
            <button
              type="button"
              class="wa-composer-btn"
              :class="{ 'wa-composer-btn--active': showQuickMessages }"
              :disabled="whatsAppOffline"
              :title="t('kanban.leadDetail.whatsapp.quickMessages.open')"
              :aria-label="t('kanban.leadDetail.whatsapp.quickMessages.open')"
              @click="toggleQuickMessages"
            >
              <svg class="wa-composer-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
            <WhatsAppQuickMessagesPopover
              v-if="showQuickMessages"
              class="absolute bottom-full left-0 mb-2 z-[10001]"
              :messages="quickMessages"
              :loading="quickMessagesLoading"
              :saving="quickMessagesSaving"
              :deleting="quickMessagesDeleting"
              :selected-id="quickMessageSelectedId"
              :on-create="createQuickMessage"
              :on-delete="deleteQuickMessage"
              @update:selected-id="quickMessageSelectedId = $event"
              @select="applyQuickMessage"
              @close="showQuickMessages = false"
            />
            <button type="button" class="wa-composer-btn" :disabled="whatsAppOffline" @click="triggerFilePick('document')">
              <svg class="wa-composer-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <button type="button" class="wa-composer-btn" :disabled="whatsAppOffline" @click="triggerFilePick('image')">
              <svg class="wa-composer-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              type="button"
              class="wa-composer-btn"
              :class="{ 'wa-composer-btn--recording': isRecording }"
              :disabled="whatsAppOffline"
              @click="toggleRecording"
            >
              <span v-if="isRecording" class="wa-composer-btn__stop" />
              <svg v-else class="wa-composer-btn__icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14a3 3 0 003-3V5a3 3 0 10-6 0v6a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 0014 0h-2zM11 19h2v3h-2v-3z" />
              </svg>
            </button>
          </div>
          <Transition name="fade">
            <div v-if="isRecording" class="wa-composer-recording">
              <span class="wa-composer-recording__dot" />
              <span class="wa-composer-recording__time">{{ recordingDuration }}s</span>
              <button type="button" class="wa-composer-recording__btn wa-composer-recording__btn--ghost" @click="cancelRecording">{{ t('kanban.leadDetail.whatsapp.cancel') }}</button>
              <button type="button" class="wa-composer-recording__btn wa-composer-recording__btn--send" @click="stopRecording">{{ t('kanban.leadDetail.whatsapp.send') }}</button>
            </div>
          </Transition>
          <div v-if="showChatComposer || (showChatbot && chatbotSession.canEndAttendance)" class="wa-composer-trailing">
            <div v-if="showChatbot && chatbotSession.canEndAttendance" class="wa-composer-end-attendance-wrap">
              <button type="button" class="wa-composer-end-attendance" :disabled="chatbotEnding" @click="endChatbotAttendance">
                <svg class="wa-composer-end-attendance__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </button>
            </div>
            <Transition name="composer-reveal">
              <button
                v-if="showChatComposer"
                type="button"
                class="wa-composer-send"
                :disabled="whatsAppOffline || !whatsAppCanQueue || !newMessage.trim()"
                @click="sendMessage"
              >
                <svg class="wa-composer-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.25">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </Transition>
          </div>
          <input ref="fileDoc" type="file" class="hidden" @change="handleFilePick('document', $event)" />
          <input ref="fileImg" type="file" accept="image/*" class="hidden" @change="handleFilePick('image', $event)" />
        </div>
        <div class="wa-composer-status">
          <span class="wa-composer-status__online" :class="{ 'wa-composer-status__online--warn': !whatsAppCanSend && whatsAppCanQueue, 'wa-composer-status__online--error': whatsAppOffline || !whatsAppCanQueue }">
            <span class="wa-composer-status__dot" />
            {{ t(composerStatusKey) }}
          </span>
          <span v-if="showChatComposer" class="wa-composer-status__hints">{{ t('kanban.leadDetail.whatsapp.hintsSend') }}</span>
        </div>
      </div>
    </template>

    <div
      v-if="previewWaImage"
      class="fixed inset-0 z-[10060] bg-black/70 flex items-center justify-center p-4"
      data-image-preview-overlay
      role="dialog"
      aria-modal="true"
      :aria-label="t('kanban.chat.previewAria')"
      @click="closeWaImagePreview"
    >
      <button
        type="button"
        class="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/75"
        :aria-label="t('common.close')"
        data-image-preview-close
        @click.stop="closeWaImagePreview"
      >
        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <img
        :src="previewWaImage.url"
        class="max-w-full max-h-[85vh] object-contain rounded-lg"
        :alt="previewWaImage.caption || t('kanban.chat.imageAlt')"
        @click.stop
      />
    </div>
  </div>
</template>

<script setup>
import { computed, toRef, ref, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EmojiPickerPopover from './EmojiPickerPopover.vue'
import WhatsAppQuickMessagesPopover from './WhatsAppQuickMessagesPopover.vue'
import WhatsAppVoiceBubble from './WhatsAppVoiceBubble.vue'
import WhatsAppYoutubePreview from './WhatsAppYoutubePreview.vue'
import { useWhatsAppChat } from '../composables/useWhatsAppChat'
import { useWhatsAppQuickMessages } from '../composables/useWhatsAppQuickMessages'
import { pushEscapeLayer } from '@/utils/escapeLayerStack'
import { formatBrazilPhoneForDisplay } from '../utils/phoneBr'
import '../styles/whatsapp-chat.css'

const props = defineProps({
  entityType: { type: String, default: 'lead' },
  entityId: { type: [Number, String], default: null },
  workspaceId: { type: [Number, String], default: null },
  contactName: { type: String, default: '' },
  contactPhone: { type: String, default: '' },
  contactLid: { type: String, default: '' },
  active: { type: Boolean, default: false },
  layout: { type: String, default: 'column' },
  showChatbot: { type: Boolean, default: false },
  columnClass: { type: String, default: '' }
})

const emit = defineEmits(['connection-update'])

const { t } = useI18n()

const phoneMissingKey = computed(() =>
  props.entityType === 'cliente'
    ? 'workspaceClienteModal.whatsapp.phoneMissing'
    : 'kanban.leadDetail.whatsapp.phoneRequired'
)

const contactPhoneDisplay = computed(() =>
  formatBrazilPhoneForDisplay(props.contactPhone) || props.contactPhone || t('kanban.leadDetail.whatsapp.noPhone')
)

/** Drawer cliente: composer sempre visual claro (tema escuro do app não altera) */
const isDrawerLightComposer = computed(() =>
  String(props.columnClass || '').includes('wa-drawer-chat')
)

const messageTextareaClass = computed(() => [
  'wa-message-textarea w-full px-4 py-2.5 pr-11 backdrop-blur-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00a884]/35 text-[15px] resize-none leading-relaxed',
  isDrawerLightComposer.value
    ? 'wa-message-textarea--drawer-light bg-white/95 border border-black/5 text-gray-900'
    : 'bg-white/95 dark:bg-[#2a3942]/95 border border-black/5 dark:border-white/10 text-gray-900 dark:text-white'
])

const chat = useWhatsAppChat({
  entityType: toRef(props, 'entityType'),
  entityId: toRef(props, 'entityId'),
  workspaceId: toRef(props, 'workspaceId'),
  contactPhone: toRef(props, 'contactPhone'),
  contactLid: toRef(props, 'contactLid'),
  active: toRef(props, 'active'),
  showChatbot: toRef(props, 'showChatbot')
})

const showQuickMessages = ref(false)

const {
  messages: quickMessages,
  loading: quickMessagesLoading,
  saving: quickMessagesSaving,
  deleting: quickMessagesDeleting,
  selectedId: quickMessageSelectedId,
  loadMessages: loadQuickMessages,
  createMessage: createQuickMessage,
  deleteMessage: deleteQuickMessage
} = useWhatsAppQuickMessages(toRef(props, 'workspaceId'))

const {
  chatMessages,
  newMessage,
  showEmojiPicker,
  messageInputEl,
  fileDoc,
  fileImg,
  autoScrollEnabled,
  chatContainer,
  previewWaImage,
  whatsAppCanSend,
  whatsAppCanQueue,
  whatsAppOffline,
  composerStatusKey,
  chatbotSession,
  chatbotEnding,
  isRecording,
  showChatComposer,
  recordingDuration,
  hasContactPhone,
  refreshWhatsAppStatus,
  loadChatHistory,
  markAllRead,
  sendMessage,
  sendTextMessage,
  sendDocumentFile,
  handleFilePick,
  triggerFilePick,
  toggleRecording,
  cancelRecording,
  stopRecording,
  toggleAutoScroll,
  toggleEmojiPicker,
  insertEmoji,
  autoResizeWhatsAppMessage,
  endChatbotAttendance,
  formatTime,
  renderWhatsAppMessage,
  getYoutubePreviews,
  messageTextWithoutYoutube,
  hasYoutubePreview,
  isMediaBubble,
  fileExtension,
  displayFileName,
  fileTypeLabel,
  fileIconClass,
  openWaImagePreview,
  closeWaImagePreview
} = chat

const onToggleEmojiPicker = () => {
  showQuickMessages.value = false
  toggleEmojiPicker()
}

const toggleQuickMessages = async () => {
  if (showQuickMessages.value) {
    showQuickMessages.value = false
    return
  }
  showEmojiPicker.value = false
  showQuickMessages.value = true
  await loadQuickMessages()
}

const applyQuickMessage = async (texto) => {
  newMessage.value = String(texto || '')
  showQuickMessages.value = false
  await nextTick()
  const input = messageInputEl.value
  input?.focus()
  autoResizeWhatsAppMessage(input)
}

const deliveryStatusIcon = (message) => {
  const st = String(message?.status || 'SENT').toUpperCase()
  if (st === 'FAILED') return '✕'
  if (st === 'PENDING') return '◷'
  return '✓'
}

const deliveryStatusTitle = (message) => {
  const st = String(message?.status || 'SENT').toUpperCase()
  if (st === 'FAILED') return t('kanban.leadDetail.whatsapp.sendFailed')
  if (st === 'PENDING') return t('kanban.leadDetail.whatsapp.sendPending')
  return t('kanban.leadDetail.whatsapp.sendSent')
}

const refreshAndEmit = async () => {
  const status = await refreshWhatsAppStatus()
  emit('connection-update', status)
  return status
}

let unregisterWaPreviewEscape = null

watch(previewWaImage, (val) => {
  if (unregisterWaPreviewEscape) {
    unregisterWaPreviewEscape()
    unregisterWaPreviewEscape = null
  }
  if (val) {
    unregisterWaPreviewEscape = pushEscapeLayer(closeWaImagePreview)
  }
})

onMounted(() => {
  if (props.active) {
    refreshAndEmit()
  }
})

onBeforeUnmount(() => {
  if (unregisterWaPreviewEscape) {
    unregisterWaPreviewEscape()
    unregisterWaPreviewEscape = null
  }
})

defineExpose({
  refreshWhatsAppStatus: refreshAndEmit,
  loadChatHistory,
  markAllRead,
  sendTextMessage,
  sendDocumentFile,
  whatsAppConnected: chat.whatsAppConnected,
  whatsAppCanSend: chat.whatsAppCanSend
})
</script>

<style scoped>
.whatsapp-chat-panel--tab {
  min-height: 0;
}
.whatsapp-chat-panel--tab .wa-chat-scroll {
  min-height: 280px;
}
</style>
